import {
  Group,
  Stack,
  Text,
  Badge,
  Box,
  ThemeIcon,
  Button,
} from '@mantine/core';
import {
  AlertCircle,
  Flag,
} from 'react-feather';
import type { CoachSessionInfo } from '../../../services/api/coachPortal';
import { RsvpDisplay } from '../../cohorts/components/RsvpDisplay';
import { formatSessionDateTime } from './helpers';
import { composeCoachSessionLabel } from '../utils/sessionLabel';
import { ParticipantLabelDivergenceIndicator, AttendanceRate } from '../../../components/common';

/**
 * MED-793 / AC3 — Prep entry-point status surfaced on each session row.
 *
 * Computed client-side from `prevSessionStatus` (SessionsTabContent passes it
 * after sorting by session_number per group). prep_status meanings:
 *   - 'locked'    → prior session not yet completed → no badge/button
 *   - 'available' → "PREP AVAILABLE" badge + "Prep Session" button
 *   - 't_minus_15' → button label flips to "Start Session" (15min before scheduled_start)
 *   - 'live'      → session in_progress → no button (AC6)
 *   - 'past'      → session already past / completed → no prep button
 */
export type SessionRowPrepStatus = 'locked' | 'available' | 't_minus_15' | 'live' | 'past';

export interface SessionRowProps {
  session: CoachSessionInfo;
  onClick: () => void;
  /** Group name, used to compose the no-topic label "Session N: {group}" (MED-1106). */
  groupName?: string;
  /** When provided, renders the AC3 prep entry-point affordances. */
  prepStatus?: SessionRowPrepStatus;
  /** Called when the coach clicks "Prep Session" / "Start Session". */
  onPrepClick?: () => void;
}

export function SessionRow({ session, onClick, groupName, prepStatus, onPrepClick }: SessionRowProps) {
  const isPast = new Date(session.scheduled_end) < new Date();
  const isCompleted = session.status === 'completed';
  const needsAttendance = isPast && !session.attendance_marked;

  // MED-1106: one shared label rule — topic → "Session N: {topic}", else
  // composed "Session N: {group}", else the stored title (workshops).
  const displayTitle = composeCoachSessionLabel({
    session_number: session.session_number,
    topic_name: session.topic_name,
    group_name: groupName,
    title: session.title,
  });

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: '16px 20px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
      className="med-mobile-stack"
    >
      {/* Left: Session number + Topic/Date */}
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }} className="med-mobile-session-left">
        {isCompleted ? (
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(76, 175, 80, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Text size="sm" fw={600} c="#4CAF50">
              {session.session_number || '?'}
            </Text>
          </Box>
        ) : (
          <ThemeIcon size={36} radius="xl" variant="light" color="gray" style={{ flexShrink: 0 }}>
            <Text size="sm" fw={600} c="var(--mantine-color-gray-6)">
              {session.session_number || '?'}
            </Text>
          </ThemeIcon>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="14px" fw={600}>
              {displayTitle}
            </Text>
            {/* MED-1147: internal-awareness indicator — participants see a
                different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={session.participant_display_differs}
              participantDisplayName={session.topic_display_name}
            />
            {session.open_concern_count > 0 && (
              <Badge size="xs" variant="light" color="orange" leftSection={<Flag size={8} />}>
                {session.open_concern_count}
              </Badge>
            )}
          </Group>
          <Text size="13px" c="dimmed" mt={2}>
            {formatSessionDateTime(session.scheduled_start, session.scheduled_end)}
          </Text>
        </div>
      </Group>

      {/* Right: Stats + Status */}
      <Group gap="md" wrap="nowrap">
        {/* RSVP display */}
        <RsvpDisplay summary={session.rsvp_summary} isFinalized={!!session.finalized_at} />

        {/* Completed session: attendance + engagement */}
        {isCompleted && session.attendance_summary ? (
          <>
            <Stack gap={0} align="center">
              {/* MED-1266: canonical rate + marking coverage via the shared component. */}
              <AttendanceRate
                rate={session.attendance_summary.percentage}
                marked={session.attendance_summary.marked}
                population={session.attendance_summary.population}
                variant="inline"
                size="sm"
                fw={600}
              />
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                Attended
              </Text>
            </Stack>
            <Stack gap={0} align="center">
              <Text size="sm" fw={600}>
                {session.avg_engagement !== null && session.avg_engagement !== undefined
                  ? Number(session.avg_engagement).toFixed(1)
                  : '—'}
              </Text>
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                Avg Engagement
              </Text>
            </Stack>
          </>
        ) : (
          isPast && (
            <>
              {needsAttendance ? (
                <Group gap="xs">
                  <ThemeIcon size="xs" variant="light" color="orange">
                    <AlertCircle size={10} />
                  </ThemeIcon>
                  <Text size="xs" c="orange" fw={500}>
                    Mark Attendance
                  </Text>
                </Group>
              ) : session.attendance_marked && session.attendance_summary ? (
                <Stack gap={0} align="center">
                  {/* MED-1266: canonical rate + marking coverage via the shared component. */}
                  <AttendanceRate
                    rate={session.attendance_summary.percentage}
                    marked={session.attendance_summary.marked}
                    population={session.attendance_summary.population}
                    variant="inline"
                    size="sm"
                    fw={600}
                  />
                  <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                    Attended
                  </Text>
                </Stack>
              ) : null}
            </>
          )
        )}

        {/* Session status badge */}
        <Badge
          size="sm"
          variant="light"
          color={
            isCompleted ? 'green' : session.status === 'scheduled' ? 'blue' : 'gray'
          }
        >
          {isCompleted ? 'Completed' : session.status.charAt(0).toUpperCase() + session.status.slice(1)}
        </Badge>

        {/* MED-793 / AC3 — Prep entry point */}
        {(prepStatus === 'available' || prepStatus === 't_minus_15') && (
          <Group gap="xs" wrap="nowrap">
            {prepStatus === 'available' && (
              <Badge
                size="xs"
                variant="light"
                color="medleyGreen"
                tt="uppercase"
                style={{ letterSpacing: '0.3px' }}
              >
                Prep Available
              </Badge>
            )}
            <Button
              size="xs"
              variant="filled"
              color="dark"
              onClick={(e) => {
                e.stopPropagation();
                onPrepClick?.();
              }}
            >
              {prepStatus === 't_minus_15' ? 'Start Session' : 'Prep Session'}
            </Button>
          </Group>
        )}
      </Group>
    </Group>
  );
}
